import isEqual from 'lodash.isequal';

import { ConfigsAction, ConfigsActionKeys } from '../actions/configs';
import { UiAction, UiActionKeys } from '../actions/ui';
import { SpreadBackgroundAction, SpreadBackgroundActionKeys } from '../actions/spreadBackground';
import { TextFieldsEditableData } from '../utils/editableTextFields/textFieldsEditableData';
import { SlidersConfig, SlideData } from '../utils/indesignEditor/editableSliders/getData';
import { ShowAloneSide } from '../constants';
import Utils from '../../../utils';

interface EditableData {
	modifiedFromBackoffice?: boolean;
	textFieldsData?: TextFieldsEditableData;
}

export interface EditableDataState {
	[k: number]: EditableData;
}

export enum PreviewMode {
	DESKTOP = 'desktop',
	PHONE = 'phone',
}

export interface Position {
	top?: number;
	left?: number;
	scale?: number;
}

export interface PositionConfig {
	desktop?: Position;
	phone?: Position;
}

export enum BackgroundMediaType {
	IMAGE = 'IMAGE',
	MOVIE = 'MOVIE',
	SLIDER = 'SLIDER',
}

export interface BackgroundImageOrMovieData {
	globalId: number;
	label: string;
	relUrl: string;
	type: BackgroundMediaType.IMAGE | BackgroundMediaType.MOVIE;
	position?: PositionConfig;
}

export interface BackgroundSliderData {
	type: BackgroundMediaType.SLIDER;
	slides: SlideData[];
}

export type BackgroundMediaData = BackgroundImageOrMovieData | BackgroundSliderData;

export interface BgStyleData {
	epubStyle: {
		backgroundMedia?: BackgroundMediaData;
		defaultBackgroundMedia?: BackgroundMediaData;
		backgroundImage?: BackgroundMediaData; // legacy option
		backgroundColor?: string; // isn't handled in Editor but can be set in Plugin
	};
}

export interface ConfigsData {
	generalData: {
		bgStyle: BgStyleData | {};
		editablePublication: EditableDataState;
	};
	pagesData: {
		bgStyle: BgStyleData | {};
		isTransparent: boolean;
		height: number;
		showAlone: boolean;
		showAloneSide?: ShowAloneSide;
		sliders?: SlidersConfig;
		cssRelUrl?: string;
		jsRelUrl?: string;
		themesCssRelUrl?: string;
	}[];
	[key: string]: any; // tslint:disable-line no-any
}

export const configsDataInitialState: ConfigsData = {
	generalData: {
		bgStyle: {},
		editablePublication: {},
	},
	pagesData: [],
};

export const configs = (state: ConfigsData = configsDataInitialState, action: ConfigsAction | SpreadBackgroundAction | UiAction): ConfigsData => {
	switch (action.type) {
		case ConfigsActionKeys.SET_CONFIGS_DATA:
			return {
				...state,
				...action.data,
			};
		case ConfigsActionKeys.SET_EDITABLE_TEXT_FIELDS_DATA: {
			const newState = Utils.deepCopy(state);
			const { page, data } = action;
			const isEmptyData = Object.values(data).every((set: Set<string>) => set.size === 0);

			if (!isEmptyData) {
				newState.generalData.editablePublication[page].textFieldsData = data;
			}

			newState.generalData.editablePublication[page].modifiedFromBackoffice = true;

			return newState;
		}
		case SpreadBackgroundActionKeys.SET_SPREAD_BACKGROUND: {
			const newState = Utils.deepCopy(state);
			const { spread, data, isGeneralBgData } = action;

			spread.forEach((page) => {
				let target = newState.pagesData[page - 1];
				let source = isGeneralBgData ? newState.generalData : target;

				// handle a case when bgStyle is set only on one page in spread in plugin
				if (Utils.isEmptyObject(source.bgStyle)) {
					const adjacentPage = spread.find(pageInSpread => pageInSpread !== page);

					if (adjacentPage) {
						source = Utils.deepCopy(state.pagesData[adjacentPage - 1]);
					} else {
						source.bgStyle = {
							epubStyle: {},
						};
					}
				}

				if (Utils.isEmptyObject(target.bgStyle)) {
					target.bgStyle = {
						epubStyle: {},
					};
				}

				if (data) {
					const {
						epubStyle: {
							defaultBackgroundMedia,
							backgroundMedia,
							backgroundImage,
						},
					} = source.bgStyle as BgStyleData;

					if (!defaultBackgroundMedia) {
						(target.bgStyle as BgStyleData).epubStyle.defaultBackgroundMedia = backgroundMedia || backgroundImage;
					}

					// remove legacy option
					if (backgroundImage) {
						delete (target.bgStyle as BgStyleData).epubStyle.backgroundImage;
					}

					(target.bgStyle as BgStyleData).epubStyle.backgroundMedia = data;

					const isEqualWithDefaultData = isEqual(data, (target.bgStyle as BgStyleData).epubStyle.defaultBackgroundMedia);

					if (isEqualWithDefaultData) {
						delete (target.bgStyle as BgStyleData).epubStyle.defaultBackgroundMedia;
					}
				} else {
					(target.bgStyle as BgStyleData).epubStyle.backgroundMedia = (source.bgStyle as BgStyleData).epubStyle.defaultBackgroundMedia as BackgroundMediaData;
					delete (target.bgStyle as BgStyleData).epubStyle.defaultBackgroundMedia;
				}
			});

			return newState;
		}
		case ConfigsActionKeys.UPDATE_EDITABLE_SLIDER_CONFIG: {
			const newState = Utils.deepCopy(state);
			const { page, id, data } = action;

			if ('slides' in newState.pagesData[page - 1].sliders![id]) {
				(newState.pagesData[page - 1].sliders![id] as { slides: SlideData[]; }).slides = data;
			} else {
				newState.pagesData[page - 1].sliders![id] = data;
			}

			return newState;
		}
		case UiActionKeys.RESET:
			return configsDataInitialState;
		default:
			return state;
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/reducer/configs.ts